import { Customer, CustomerFormData, CustomerStats, PurchaseHistory } from '../types';

// Base URL de la API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Función para obtener el token de autenticación
const getAuthToken = (): string | null => {
  return localStorage.getItem('accessToken');
};

// Función para hacer peticiones autenticadas
const apiRequest = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const token = getAuthToken();
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
    throw new Error(errorData.error || `HTTP Error: ${response.status}`);
  }

  return response.json();
};

class CustomersService {
  async getCustomers(includeInactive = false): Promise<Customer[]> {
    const params = includeInactive ? '?include_inactive=true' : '';
    const response = await apiRequest(`/customers${params}`);
    return response.data;
  }

  async getCustomerStats(): Promise<CustomerStats> {
    const response = await apiRequest('/customers/stats');
    return response.data;
  }

  async getCustomerById(id: string): Promise<Customer | null> {
    try {
      const response = await apiRequest(`/customers/${id}`);
      return response.data;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async createCustomer(data: CustomerFormData): Promise<Customer> {
    const response = await apiRequest('/customers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async updateCustomer(id: string, data: CustomerFormData): Promise<Customer> {
    const response = await apiRequest(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async deleteCustomer(id: string): Promise<void> {
    await apiRequest(`/customers/${id}`, {
      method: 'DELETE',
    });
  }

  async getCustomerPurchaseHistory(customerId: string): Promise<PurchaseHistory[]> {
    // TODO: Implementar cuando esté disponible en el backend
    return [];
  }

  async addLoyaltyPoints(customerId: string, points: number, operation: 'add' | 'subtract' | 'set' = 'add'): Promise<Customer> {
    const response = await apiRequest(`/customers/${customerId}/loyalty-points`, {
      method: 'PUT',
      body: JSON.stringify({ points, operation }),
    });
    
    // Obtener el cliente actualizado
    return this.getCustomerById(customerId).then(customer => {
      if (!customer) throw new Error('Cliente no encontrado');
      return customer;
    });
  }

  async searchCustomers(query: string): Promise<Customer[]> {
    const response = await apiRequest(`/customers?search=${encodeURIComponent(query)}`);
    return response.data;
  }

  async reactivateCustomer(id: string): Promise<Customer> {
    const response = await apiRequest(`/customers/${id}/reactivate`, {
      method: 'PUT',
    });
    return response.data;
  }
}

export const customersService = new CustomersService();

// Export individual functions for easier mocking in tests
export const getCustomers = (includeInactive = false) => customersService.getCustomers(includeInactive);
export const getCustomerStats = () => customersService.getCustomerStats();
export const getCustomerById = (id: string) => customersService.getCustomerById(id);
export const createCustomer = (data: CustomerFormData) => customersService.createCustomer(data);
export const updateCustomer = (id: string, data: CustomerFormData) => customersService.updateCustomer(id, data);
export const deleteCustomer = (id: string) => customersService.deleteCustomer(id);
export const getCustomerPurchaseHistory = (customerId: string) => customersService.getCustomerPurchaseHistory(customerId);
export const addLoyaltyPoints = (customerId: string, points: number) => customersService.addLoyaltyPoints(customerId, points);
export const searchCustomers = (query: string) => customersService.searchCustomers(query);
export const reactivateCustomer = (id: string) => customersService.reactivateCustomer(id);